/* eslint-disable ember/classic-decorator-hooks, ember/no-computed-properties-in-native-classes, no-restricted-properties, no-unused-vars, warp-drive/no-legacy-request-patterns */
// Extends from
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
// Ember imports
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { union } from '@ember/object/computed';
// External imports
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import SnapshotType from '@linc-technologies/linced-common/enums/snapshot-type';
import SnapshotPdfType from '@linc-technologies/linced-common/enums/snapshot-pdf-type';
import {
  downloadSnapshotPdf,
  openSnapshotPdf,
} from '@linc-technologies/linced-common/utils/snapshots';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';

export default class ReportSnapshotsController extends Controller {
  queryParams = ['type'];
  SnapshotType = SnapshotType;
  SnapshotPdfType = SnapshotPdfType;
  Jurisdiction = Jurisdiction;

  // Services
  @service
  media;

  @service
  permissions;

  @service
  session;

  @service
  store;

  @service
  toaster;

  // Attributes
  @tracked
  newAutoSnapshots = [];

  @tracked
  newOnDemandSnapshots = [];
  showAutoSnapshots = false;

  // Filtering auto-generated reports
  @tracked
  type = SnapshotType.TERMLY;

  // Computed:
  @computed('session.scopes.[]')
  get canRegenerate() {
    // Limit the overwrite functionality only to Hero Managers and higher
    return this.permissions.meet(Scopes.LINCED_MANAGER);
  }

  @union('model.autoSnapshots', 'newAutoSnapshots')
  allAutoSnapshots;

  @union('model.customSnapshots', 'newOnDemandSnapshots')
  allOnDemandSnapshots;

  @tracked
  endDate;

  @tracked
  selectedType;

  @tracked
  startDate;

  @tracked
  term;

  @tracked
  year;

  // removedDuplicateAutoSnapshots filters out auto-generated snapshots of the
  // selected type that have duplicate date ranges and instead uses the newest
  // one.
  // Native getter rather than a computed: `metadata` is an untyped attr (a
  // plain object), so a two-level @each key is not allowed in Ember and would
  // not track mutation of the POJO anyway.
  get removedDuplicateAutoSnapshots() {
    if (isEmpty(this.allAutoSnapshots)) {
      return [];
    }

    let seen = new Map();
    this.allAutoSnapshots.forEach((snapshot) => {
      let { startDate, endDate } = snapshot.metadata;
      let key = `${startDate}|${endDate}`;

      // Only replace if this one is newer
      if (!seen.has(key) || snapshot.createTime > seen.get(key).createTime) {
        seen.set(key, snapshot);
      }
    });

    return Array.from(seen.values());
  }

  // Hooks
  init() {
    super.init(...arguments);
    // Bind the context for passing the function to the generate-snapshot and generate-report components
    this.addSnapshot = this.addSnapshot.bind(this);
    this.addReportToSnapshot = this.addReportToSnapshot.bind(this);
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.newAutoSnapshots = [];
    this.newOnDemandSnapshots = [];
  }

  // Functions
  // addSnapshot adds the newly generated snapshot to the list of all
  // if we're on the same tab as the one being generated,
  // or change tabs if the snapshot is of a different type
  addSnapshot(snapshot) {
    // On-demand snapshots
    if (snapshot.metadata.type === SnapshotType.ON_DEMAND) {
      this.newOnDemandSnapshots = [...this.newOnDemandSnapshots, snapshot];
      return;
    }
    // Regenerated auto-generated snapshots
    if (snapshot.metadata.type === this.type) {
      // We're generating a snapshot of the same type that the tab is on
      this.newAutoSnapshots = [...this.newAutoSnapshots, snapshot];
      return;
    }
    // We're generating a snapshot of a different from selected type, so
    // just change the filter, and the model will re-load
    // with the new snapshot in there
    this.type = snapshot.get('metadata.type');
  }

  // addReportToSnapshot adds the newly generated PDF report to the snapshot
  // until the model gets refreshed
  addReportToSnapshot(snapshot, reportId) {
    if (!snapshot.pdfs.any((pdf) => pdf.mediaId === reportId)) {
      let newPdf = this.store.createFragment('snapshots/pdf', {
        mediaId: reportId,
      });

      snapshot.pdfs.pushObject(newPdf);
    }
  }

  @action
  downloadReport(pdfReport, snapshot) {
    return this.media
      .getFileBlob(pdfReport.mediaId)
      .then((url) => {
        downloadSnapshotPdf(url, this.model.learner, snapshot);
      })
      .catch(() => {
        this.toaster.error('Failed to download the PDF. Please try again');
      });
  }

  @action
  async openReportInTab(pdfReport) {
    let mediaId = pdfReport.mediaId.id || pdfReport.mediaId;
    try {
      let file = await this.store.findRecord('media', mediaId);
      openSnapshotPdf(file);
    } catch (error) {
      this.toaster.error('Failed to open the PDF. Please try again');
    }
  }

  @action
  copyToRegenerate(snapshot) {
    this.startDate = snapshot.metadata.startDate;
    this.endDate = snapshot.metadata.endDate;
    this.term = snapshot.metadata.term;
    this.year = snapshot.metadata.year;
    this.selectedType = snapshot.metadata.type;
  }
}
